import React, { useState, useEffect, useRef } from 'react';
import { InformePericial, Videoperitacion, FaseInforme } from '../../types';
import { informeService } from '../../services/api';
import { FiFileText, FiPlus, FiTrash2, FiLoader } from 'react-icons/fi';
import { getAbsoluteUrl } from '../../utils/url';
import './EncargoForm.css';

interface SubidaArchivosFaseProps {
    informe?: InformePericial;
    onUpdate: (informe: InformePericial, targetFase?: FaseInforme) => void;
}

const SubidaArchivosFase: React.FC<SubidaArchivosFaseProps> = ({ informe, onUpdate }) => {
    const [uploading, setUploading] = useState(false);
    const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [videoperitacion, setVideoperitacion] = useState<Videoperitacion | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (informe) {
            prvCargarVideoperitacion();
        }
    }, [informe]);

    const prvCargarVideoperitacion = async () => {
        if (!informe) return;
        try {
            const response = await informeService.obtenerVideoperitacion(informe.id);
            setVideoperitacion(response.data);
        } catch (err: any) {
            if (err.response && err.response.status === 404) {
                setVideoperitacion(null);
            } else {
                setError('No se pudo comprobar el estado de los archivos. Revisa tu conexión.');
                console.error('Error obteniendo videoperitación:', err);
            }
        }
    };

    const prvHandleSiguienteFase = async () => {
        if (!informe) return;
        let updatedInforme = informe;
        try {
            const res = await informeService.cambiarFase(informe.id, 'grabacion');
            updatedInforme = res.data;
        } catch (e) {
            console.error('Error al cambiar de fase:', e);
        }
        onUpdate(updatedInforme, 'grabacion');
    };

    const prvHandleAddClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const prvHandleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!informe) return;
        if (e.target.files && e.target.files.length > 0) {
            const selectedFiles = Array.from(e.target.files);
            const invalidFiles = selectedFiles.filter(f => !f.name.toLowerCase().endsWith('.pdf'));
            if (invalidFiles.length > 0) {
                setError('Por favor selecciona solo archivos .pdf');
                return;
            }

            setUploading(true);
            setError('');

            try {
                const response = await informeService.subirVideoperitacion(informe.id, selectedFiles);
                setVideoperitacion(response.data);
            } catch (err: any) {
                console.error('Error al subir archivos:', err);
                setError(err.response?.data?.error || 'Error al subir los archivos');
            } finally {
                setUploading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const prvHandleDelete = async (url: string) => {
        if (!informe) return;
        if (!window.confirm('¿Estás seguro de que deseas eliminar este archivo?')) return;

        setDeletingUrl(url);
        setError('');

        try {
            const response = await informeService.eliminarArchivoVideoperitacion(informe.id, url);
            setVideoperitacion(response.data);
        } catch (err: any) {
            console.error('Error al eliminar archivo:', err);
            setError(err.response?.data?.error || 'Error al eliminar el archivo');
        } finally {
            setDeletingUrl(null);
        }
    };

    const prvGetUploadedPdfs = (): string[] => {
        if (!videoperitacion || !videoperitacion.zip_file_url) return [];
        try {
            const parsed = JSON.parse(videoperitacion.zip_file_url);
            if (Array.isArray(parsed)) {
                return parsed;
            }
            return [videoperitacion.zip_file_url];
        } catch (e) {
            return [videoperitacion.zip_file_url];
        }
    };

    const pdfs = prvGetUploadedPdfs();

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flexShrink: 0, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="section-title" style={{ margin: 0 }}>Archivos Adicionales</h3>
                {informe && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={prvHandleSiguienteFase}
                    >
                        Siguiente Fase
                    </button>
                )}
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '15px' }}>Documentos del Informe</span>
                    
                    <div>
                        <input
                            type="file"
                            accept=".pdf"
                            multiple
                            ref={fileInputRef}
                            onChange={prvHandleFileChange}
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={prvHandleAddClick}
                            disabled={uploading}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '14px', borderRadius: '6px' }}
                        >
                            {uploading ? (
                                <>
                                    <FiLoader className="animate-spin" />
                                    <span>Subiendo...</span>
                                </>
                            ) : (
                                <>
                                    <FiPlus style={{ fontSize: '16px' }} />
                                    <span>Añadir PDF</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="error-message" style={{ margin: '16px 20px', padding: '12px 16px', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '14px' }}>
                        {error}
                    </div>
                )}

                <div style={{ padding: '0 20px' }}>
                    {pdfs.length === 0 ? (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                            <FiFileText style={{ fontSize: '36px', margin: '0 auto 12px', color: '#94a3b8', display: 'block' }} />
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>No hay archivos subidos en este informe pericial.</p>
                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>Haz clic en "Añadir PDF" para subir un documento.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {pdfs.map((url, index) => {
                                const filename = url.split('/').pop() || `Documento_${index + 1}.pdf`;
                                const cleanFilename = filename.replace(/_\d+(?:_\d+)?\.pdf$/i, '.pdf');
                                const isDeleting = deletingUrl === url;

                                return (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '14px 0',
                                            borderBottom: index < pdfs.length - 1 ? '1px solid #f1f5f9' : 'none'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                            <FiFileText style={{ color: '#ef4444', fontSize: '20px', flexShrink: 0 }} />
                                            <a
                                                href={getAbsoluteUrl(url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    fontSize: '14px',
                                                    color: '#3b82f6',
                                                    fontWeight: 500,
                                                    textDecoration: 'none',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                className="hover:underline"
                                            >
                                                {cleanFilename}
                                            </a>
                                        </div>
                                        
                                        <button
                                            type="button"
                                            onClick={() => prvHandleDelete(url)}
                                            disabled={isDeleting || uploading}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: isDeleting ? '#94a3b8' : '#64748b',
                                                cursor: isDeleting ? 'default' : 'pointer',
                                                padding: '6px',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isDeleting) e.currentTarget.style.color = '#ef4444';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isDeleting) e.currentTarget.style.color = '#64748b';
                                            }}
                                            title="Eliminar archivo"
                                        >
                                            {isDeleting ? (
                                                <FiLoader className="animate-spin" style={{ fontSize: '16px' }} />
                                            ) : (
                                                <FiTrash2 style={{ fontSize: '16px' }} />
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubidaArchivosFase;
